import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { CreateBusinessForm } from '../../types/business';
import toast from 'react-hot-toast';
import { Building2, User, Mail, Lock, Phone, MapPin, Plus, Check } from 'lucide-react';

const CreateBusiness: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<CreateBusinessForm>({
        businessName: '',
        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
        adminPassword: '',
        confirmPassword: '',
        phone: '',
        address: '',
    });
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!acceptedTerms) {
            toast.error('Debes aceptar los términos y condiciones');
            return;
        }

        if (formData.adminPassword !== formData.confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        if (formData.adminPassword.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        setLoading(true);

        try {
            console.log('📝 Paso 1: Creando usuario en Firebase Auth...');
            const userCredential = await createUserWithEmailAndPassword(
                auth,
                formData.adminEmail,
                formData.adminPassword
            );
            const adminUserId = userCredential.user.uid;
            console.log('✅ Usuario creado:', adminUserId);

            console.log('📝 Paso 2: Creando negocio en Firestore (callable)...');
            const createBusinessFn = httpsCallable(functions, 'createBusinessCallable');
            const result = await createBusinessFn({
                businessName: formData.businessName,
                adminFirstName: formData.adminFirstName,
                adminLastName: formData.adminLastName,
                adminEmail: formData.adminEmail,
                phone: formData.phone,
                address: formData.address,
                origin: window.location.origin,
            }) as any;

            if (!result?.data?.success) {
                throw new Error(result?.data?.error || 'No se pudo crear el negocio');
            }

            console.log('✅ Negocio creado:', result.data.businessId);

            toast.success('¡Negocio creado! Inicia sesión para continuar.');
            try {
                await auth.signOut();
            } catch {}
            window.location.replace('/login?tab=login');
            return;
        } catch (error: any) {
            console.error('❌ Error creating business:', error);
            if (error.code === 'auth/email-already-in-use') {
                toast.error('Este email ya está registrado');
            } else {
                toast.error('Error al crear el negocio: ' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información del Negocio */}
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-amber-400" />
                    <h3 className="text-lg font-semibold text-white">
                        Información del Negocio
                    </h3>
                </div>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="businessName" className="block text-sm font-medium text-gray-300 mb-2">
                            Nombre del Negocio *
                        </label>
                        <input
                            id="businessName"
                            name="businessName"
                            type="text"
                            required
                            value={formData.businessName}
                            onChange={handleChange}
                            className="block w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                            placeholder="Ej: Gimnasio Dragón"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                                Teléfono
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Phone className="h-5 w-5 text-gray-500" />
                                </div>
                                <input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-300 mb-2">
                                Dirección
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MapPin className="h-5 w-5 text-gray-500" />
                                </div>
                                <input
                                    id="address"
                                    name="address"
                                    type="text"
                                    value={formData.address}
                                    onChange={handleChange}
                                    className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Datos del Administrador */}
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <User className="w-5 h-5 text-amber-400" />
                    <h3 className="text-lg font-semibold text-white">
                        Datos del Administrador
                    </h3>
                </div>

                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="adminFirstName" className="block text-sm font-medium text-gray-300 mb-2">
                                Nombre *
                            </label>
                            <input
                                id="adminFirstName"
                                name="adminFirstName"
                                type="text"
                                required
                                value={formData.adminFirstName}
                                onChange={handleChange}
                                className="block w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div>
                            <label htmlFor="adminLastName" className="block text-sm font-medium text-gray-300 mb-2">
                                Apellido *
                            </label>
                            <input
                                id="adminLastName"
                                name="adminLastName"
                                type="text"
                                required
                                value={formData.adminLastName}
                                onChange={handleChange}
                                className="block w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-300 mb-2">
                            Email *
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-500" />
                            </div>
                            <input
                                id="adminEmail"
                                name="adminEmail"
                                type="email"
                                required
                                value={formData.adminEmail}
                                onChange={handleChange}
                                className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="adminPassword" className="block text-sm font-medium text-gray-300 mb-2">
                            Contraseña *
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-500" />
                            </div>
                            <input
                                id="adminPassword"
                                name="adminPassword"
                                type="password"
                                required
                                value={formData.adminPassword}
                                onChange={handleChange}
                                className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                placeholder="Mínimo 6 caracteres"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                            Confirmar Contraseña *
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-500" />
                            </div>
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                required
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Checkbox de aceptación de términos */}
            <div className="flex items-start gap-3">
                <button
                    type="button"
                    onClick={() => setAcceptedTerms(!acceptedTerms)}
                    className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-all ${
                        acceptedTerms 
                            ? 'bg-amber-500 border-amber-500' 
                            : 'bg-transparent border-gray-600 hover:border-amber-500'
                    }`}
                >
                    {acceptedTerms && <Check className="w-3 h-3 text-white" />}
                </button>
                <label className="text-sm text-gray-400 leading-relaxed">
                    He leído y acepto los{' '}
                    <Link 
                        to="/terms" 
                        target="_blank"
                        className="text-amber-400 hover:text-amber-300 underline"
                    >
                        Términos y Condiciones
                    </Link>{' '}
                    y la{' '}
                    <Link 
                        to="/privacy" 
                        target="_blank"
                        className="text-amber-400 hover:text-amber-300 underline"
                    >
                        Política de Privacidad
                    </Link>
                    . Consiento el tratamiento de mis datos personales según lo descrito.
                </label>
            </div>

            <button
                type="submit"
                disabled={loading || !acceptedTerms}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl shadow-lg shadow-amber-500/25 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
                {loading ? (
                    <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Creando negocio...
                    </>
                ) : (
                    <>
                        <Plus className="w-5 h-5" />
                        Crear Negocio
                    </>
                )}
            </button>
        </form>
    );
};

export default CreateBusiness;
